/**
 * TopicGroupItemActions — per-email action toolbar for the smart inbox view.
 *
 * Extracts the same ActionPill groups from EmailDetailHeader into a compact,
 * reusable component that can be rendered per-item inside topic groups.
 */
import React, { useState, useRef } from 'react';
import {
  Reply, ReplyAll, Forward, Archive, Trash2,
  Inbox, CalendarPlus, Columns3, ThumbsDown, Flag,
} from 'lucide-react';
import { ICON_PX, ActionPill, Tooltip, TooltipTrigger, TooltipContent } from '../../../ui';
import { useInbox } from '../InboxContext';

// ── Popover shared styles — fixed position to escape scroll containers ───────
const POP_BASE = {
  position: 'fixed', zIndex: 1050,
  background: 'var(--white)', borderRadius: 9,
  boxShadow: '0 4px 18px rgba(0,0,0,0.16)', border: '1px solid var(--surface)',
};

/** Compute fixed popover position from a button ref */
function popPos(ref) {
  if (!ref?.current) return {};
  const r = ref.current.getBoundingClientRect();
  return {
    top: Math.min(r.bottom + 6, window.innerHeight - 320),
    left: Math.min(r.left, window.innerWidth - 280),
  };
}

// ── Mini calendar ────────────────────────────────────────────────────────────
const MC_DAYS = ['S','M','T','W','T','F','S'];
function MiniCal({ value, onChange }) {
  const todayObj = new Date();
  const init = () => {
    if (value) { const [y, m] = value.split('-').map(Number); return new Date(y, m - 1, 1); }
    return new Date(todayObj.getFullYear(), todayObj.getMonth(), 1);
  };
  const [view, setView] = useState(init);
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const dim = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  const toStr = (d) => `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <button type="button" onClick={() => setView(new Date(year, month - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>{'\u2039'}</button>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy-deep)' }}>{view.toLocaleString('en-US', { month: 'long' })} {year}</span>
        <button type="button" onClick={() => setView(new Date(year, month + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--navy-deep)' }}>{'\u203A'}</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
        {MC_DAYS.map((d, i) => <div key={i} style={{ textAlign: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', padding: '2px 0' }}>{d}</div>)}
        {cells.map((d, i) => d == null
          ? <div key={`e${i}`} />
          : <button key={d} type="button" onClick={() => onChange(toStr(d))} style={{
              textAlign: 'center', fontSize: 11, padding: '4px 0', borderRadius: 4, border: 'none', cursor: 'pointer',
              background: value === toStr(d) ? 'var(--aqua)' : 'transparent',
              color: value === toStr(d) ? 'white' : 'var(--navy-deep)', fontWeight: value === toStr(d) ? 700 : 400,
            }}>{d}</button>
        )}
      </div>
    </div>
  );
}

// ── Priority colors ──────────────────────────────────────────────────────────
const PRIORITY_COLOR = {
  critical: { border: 'var(--red-critical)', text: 'var(--red-critical)' },
  high:     { border: 'var(--red-critical)', text: 'var(--red-critical)' },
  medium:   { border: 'var(--yellow)',       text: 'var(--yellow)' },
  low:      { border: 'var(--green)',        text: 'var(--green)' },
};

/**
 * scope="group" — Queue/Schedule/Board + Dismiss/Flag Priority (triage actions)
 * scope="email" — Reply/ReplyAll/Forward + Archive/Delete + Flag toggle (per-message)
 * scope omitted — all actions (backward compat)
 */
function TopicGroupItemActions({ email, scope }) {
  const { actions, startReply } = useInbox();

  const [showSchedule, setShowSchedule] = useState(false);
  const [showBoard, setShowBoard] = useState(false);
  const [showFlag, setShowFlag] = useState(false);
  const schedRef = useRef(null);
  const boardRef = useRef(null);
  const flagRef = useRef(null);
  const [schedDate, setSchedDate] = useState('');
  const [flagPriority, setFlagPriority] = useState('');
  const [flagFeedback, setFlagFeedback] = useState('');

  if (!email) return null;

  // Use thread-level actions for group scope, per-email actions otherwise
  const isGroup = scope === 'group';
  const doQueue = isGroup ? actions.queueThread : actions.queueEmail;
  const doSchedule = isGroup ? actions.scheduleThread : actions.scheduleEmail;
  const doBoard = isGroup ? actions.boardThread : actions.boardEmail;
  const doDismiss = isGroup ? actions.dismissThread : actions.dismissEmail;

  const closeAll = () => { setShowSchedule(false); setShowBoard(false); setShowFlag(false); };
  const openPop = (setter) => { closeAll(); setter(true); };

  const confirmSchedule = () => {
    if (!schedDate) return;
    doSchedule(email.id, schedDate);
    setShowSchedule(false);
  };

  const confirmBoard = (swimlane) => {
    doBoard(email.id, swimlane);
    setShowBoard(false);
  };

  const submitFlag = () => {
    if (!flagPriority) return;
    setShowFlag(false);
    setFlagPriority('');
    setFlagFeedback('');
  };

  const showEmail = !scope || scope === 'email';
  const showGroup = !scope || scope === 'group';

  return (
    <div className="flex items-center gap-1 flex-wrap mt-1.5">
      {/* Reply / Reply All / Forward — per-email */}
      {showEmail && <ActionPill>
        <ActionPill.Button tip="Reply" color="var(--royal)" onClick={() => startReply(email, 'reply')}>
          <Reply size={ICON_PX.md} />
        </ActionPill.Button>
        <ActionPill.Button tip="Reply All" color="var(--royal)" onClick={() => startReply(email, 'replyAll')}>
          <ReplyAll size={ICON_PX.md} />
        </ActionPill.Button>
        <ActionPill.Button tip="Forward" color="var(--royal)" onClick={() => startReply(email, 'forward')}>
          <Forward size={ICON_PX.md} />
        </ActionPill.Button>
      </ActionPill>}

      {/* Queue / Schedule / Board — group-level */}
      {showGroup && <ActionPill>
        <ActionPill.Button
          tip="Queue it"
          color="var(--aqua)"
          onClick={() => doQueue(email.id)}
          disabled={email.queueStatus === 'queued'}
        >
          <Inbox size={ICON_PX.md} />
        </ActionPill.Button>
        <span ref={schedRef}>
          <ActionPill.Button tip="Schedule it" color="var(--green)" onClick={() => openPop(setShowSchedule)}>
            <CalendarPlus size={ICON_PX.md} />
          </ActionPill.Button>
          {showSchedule && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowSchedule(false)} />
              <div style={{ ...POP_BASE, ...popPos(schedRef), padding: 12, minWidth: 250 }}>
                <MiniCal value={schedDate} onChange={setSchedDate} />
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button type="button" onClick={confirmSchedule}
                    style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                      background: schedDate ? 'var(--aqua)' : 'var(--surface)',
                      color: schedDate ? 'white' : 'var(--text-muted)', cursor: schedDate ? 'pointer' : 'default' }}>Confirm</button>
                  <button type="button" onClick={() => setShowSchedule(false)}
                    style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}>Cancel</button>
                </div>
              </div>
            </>
          )}
        </span>
        <span ref={boardRef}>
          <ActionPill.Button tip="Add to Board" color="var(--royal)" onClick={() => openPop(setShowBoard)}>
            <Columns3 size={ICON_PX.md} />
          </ActionPill.Button>
          {showBoard && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowBoard(false)} />
              <div style={{ ...POP_BASE, ...popPos(boardRef), padding: 10, minWidth: 150 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy-deep)', marginBottom: 6, paddingLeft: 2 }}>Add to swimlane</div>
                {[
                  { key: 'today',     label: 'Today',       color: 'var(--red-critical)' },
                  { key: 'scheduled', label: 'Scheduled',   color: 'var(--yellow)' },
                  { key: 'parking',   label: 'Parking Lot', color: 'var(--text-muted)' },
                ].map(({ key, label, color }) => (
                  <button key={key} type="button" onClick={() => confirmBoard(key)}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', borderRadius: 5, border: 'none',
                      fontSize: 12, fontWeight: 600, color, cursor: 'pointer', background: 'transparent', marginBottom: 1 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-warm)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >{label}</button>
                ))}
              </div>
            </>
          )}
        </span>
      </ActionPill>}

      {/* Dismiss / Flag Priority — group-level */}
      {showGroup && <ActionPill>
        <ActionPill.Button tip="Not important" color="var(--text-muted)" onClick={() => doDismiss(email.id)}>
          <ThumbsDown size={ICON_PX.md} />
        </ActionPill.Button>
        <span ref={flagRef}>
          <ActionPill.Button tip="Change Priority" color="var(--orange-warn)" onClick={() => openPop(setShowFlag)}>
            <Flag size={ICON_PX.md} />
          </ActionPill.Button>
          {showFlag && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 1049 }} onClick={() => setShowFlag(false)} />
              <div style={{ ...POP_BASE, ...popPos(flagRef), padding: 14, minWidth: 240 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy-deep)', marginBottom: 4 }}>Flag Priority Issue</div>
                <div style={{ fontSize: 10, color: 'var(--text-dark-secondary)', marginBottom: 8 }}>Suggest a corrected priority.</div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                  {['critical', 'high', 'medium', 'low'].map((p) => {
                    const c = PRIORITY_COLOR[p] || PRIORITY_COLOR.low;
                    const sel = flagPriority === p;
                    return (
                      <button key={p} type="button" onClick={() => setFlagPriority(p)}
                        style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
                          padding: '3px 10px', borderRadius: 4, cursor: 'pointer',
                          border: `1px solid ${c.border}`, background: sel ? c.border : 'transparent',
                          color: sel ? 'white' : c.text, transition: 'background 0.1s' }}>{p}</button>
                    );
                  })}
                </div>
                <textarea placeholder="Why? (optional)" value={flagFeedback} onChange={(e) => setFlagFeedback(e.target.value)} rows={2}
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 11, padding: '5px 7px', border: '1px solid var(--surface)', borderRadius: 5, resize: 'none', color: 'var(--navy-deep)', outline: 'none', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button type="button" onClick={submitFlag}
                    style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: 'none',
                      background: flagPriority ? 'var(--orange-warn)' : 'var(--surface)',
                      color: flagPriority ? 'white' : 'var(--text-muted)', cursor: flagPriority ? 'pointer' : 'default' }}>Submit</button>
                  <button type="button" onClick={() => setShowFlag(false)}
                    style={{ flex: 1, fontSize: 11, fontWeight: 600, padding: '5px 0', borderRadius: 5, border: '1px solid var(--surface)', background: 'none', cursor: 'pointer', color: 'var(--text-dark-secondary)' }}>Cancel</button>
                </div>
              </div>
            </>
          )}
        </span>
      </ActionPill>}

      {/* Archive / Delete — per-email */}
      {showEmail && <ActionPill>
        <ActionPill.Button tip="Archive" color="var(--green)" onClick={() => actions.archiveEmail(email.id)}>
          <Archive size={ICON_PX.md} />
        </ActionPill.Button>
        <ActionPill.Button tip="Delete" color="var(--red-critical)" onClick={() => actions.trashEmail(email.id)}>
          <Trash2 size={ICON_PX.md} />
        </ActionPill.Button>
      </ActionPill>}

      {/* Flag toggle — per-email */}
      {showEmail && <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => actions.toggleStar(email.id)}
            className="flex items-center p-0.5 rounded hover:bg-surface-warm transition-colors"
          >
            <Flag
              size={ICON_PX.md}
              style={{ color: email.isStarred ? 'var(--red-critical)' : 'var(--text-muted)' }}
              fill={email.isStarred ? 'var(--red-critical)' : 'none'}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{email.isStarred ? 'Unflag' : 'Flag'}</TooltipContent>
      </Tooltip>}
    </div>
  );
}

export { TopicGroupItemActions };
